import {
  ANALYTICS_INTERVALS,
  ANALYTICS_METRIC_NAMES,
  ANALYTICS_SPLIT_NAMES,
  type AnalyticsInterval,
  type AnalyticsMetricName,
  type AnalyticsSplitName,
} from "@lafayette/schemas";
import { useCallback, useMemo } from "react";
import { useLocation, useSearchParams } from "react-router";

import { METRIC_INFO } from "./metric-catalog";
import type { Zoom } from "./zoom-math";

// The state of the Explore tab lives in the address: `?tab=explore&range=90d&metric=sessions&split=platform&interval=day&compare=1&from=..&to=..`.
// A zoomed view can be shared, a reload keeps it, and Back goes to the zoom before. A value that is not on the closed lists is the default,
// never an error. A change of a control REPLACES the entry; a zoom ADDS one, so Back restores the window before.

const DEFAULT_METRIC: AnalyticsMetricName = "sessions";

export type ExploreAddress = Readonly<{
  metric: AnalyticsMetricName;
  split: AnalyticsSplitName;
  /** `null`: the server picks one from the span of the window. */
  interval: AnalyticsInterval | null;
  compare: boolean;
  zoom: Zoom | null;
}>;

const asMember = <T extends string>(list: readonly T[], value: string | null): T | null =>
  list.includes(value as T) ? (value as T) : null;

function asZoom(from: string | null, to: string | null): Zoom | null {
  if (from === null || to === null) return null;
  const start = Date.parse(from);
  const end = Date.parse(to);
  if (Number.isNaN(start) || Number.isNaN(end) || end <= start) return null;
  return { from: new Date(start).toISOString(), to: new Date(end).toISOString() };
}

export function parseExploreAddress(search: URLSearchParams): ExploreAddress {
  const metric = asMember(ANALYTICS_METRIC_NAMES, search.get("metric")) ?? DEFAULT_METRIC;
  const split = asMember(ANALYTICS_SPLIT_NAMES, search.get("split")) ?? "none";
  return {
    metric,
    split: METRIC_INFO[metric].splits.includes(split) ? split : "none",
    interval: asMember(ANALYTICS_INTERVALS, search.get("interval")),
    compare: search.get("compare") === "1",
    zoom: asZoom(search.get("from"), search.get("to")),
  };
}

export type ExploreControls = Partial<
  Readonly<{
    metric: AnalyticsMetricName;
    split: AnalyticsSplitName;
    interval: AnalyticsInterval | null;
    compare: boolean;
    zoom: Zoom | null;
  }>
>;

/** The address after a change: the other parameters (the tab, the range) stay. A new metric drops a split that it does not allow. A new zoom drops the interval, so the server picks one that fits. */
export function withExploreControls(
  current: URLSearchParams,
  patch: ExploreControls,
): URLSearchParams {
  const next = new URLSearchParams(current);
  if (patch.metric !== undefined) applyMetric(next, current, patch);
  if (patch.split !== undefined)
    setOrDelete(next, "split", patch.split === "none" ? null : patch.split);
  if (patch.interval !== undefined) setOrDelete(next, "interval", patch.interval);
  if (patch.compare !== undefined) setOrDelete(next, "compare", patch.compare ? "1" : null);
  if (patch.zoom !== undefined) applyZoom(next, patch);
  return next;
}

function setOrDelete(params: URLSearchParams, key: string, value: string | null): void {
  if (value === null) params.delete(key);
  else params.set(key, value);
}

/** A new metric drops a split that it does not allow. */
function applyMetric(
  next: URLSearchParams,
  current: URLSearchParams,
  patch: ExploreControls,
): void {
  if (patch.metric === undefined) return;
  next.set("metric", patch.metric);
  const split = patch.split ?? parseExploreAddress(current).split;
  if (!METRIC_INFO[patch.metric].splits.includes(split)) next.delete("split");
}

/** A new zoom drops the interval, so the server picks one that fits, unless the change names one. */
function applyZoom(next: URLSearchParams, patch: ExploreControls): void {
  setOrDelete(next, "from", patch.zoom?.from ?? null);
  setOrDelete(next, "to", patch.zoom?.to ?? null);
  if (patch.interval === undefined) next.delete("interval");
}

/** The address of Explore with a metric, from the Overview: the range stays, the zoom and the split do not. */
export function exploreHref(
  metric: AnalyticsMetricName,
  range: string,
  split: AnalyticsSplitName = "none",
): string {
  return `?tab=explore&range=${range}&metric=${metric}${split === "none" ? "" : `&split=${split}`}`;
}

/** What a history entry of a zoom remembers: how many zooms deep the view is, so Esc can go back exactly one. */
type ZoomState = Readonly<{ zoomDepth?: number }> | null;

export type ExploreNavigation = ExploreAddress &
  Readonly<{
    setControls: (patch: ExploreControls) => void;
    /** A zoom into a window: a new history entry. `replace` changes the window of the level the view is at (the navigator buttons). */
    zoomTo: (zoom: Zoom, mode?: "push" | "replace") => void;
    clearZoom: () => void;
    /** Esc: back one level of zoom. At the first level it clears the zoom; with no zoom it does nothing. */
    goUp: () => void;
    depth: number;
  }>;

export function useExploreAddress(): ExploreNavigation {
  const [search, setSearch] = useSearchParams();
  const location = useLocation();
  const depth = (location.state as ZoomState)?.zoomDepth ?? 0;
  const address = useMemo(() => parseExploreAddress(search), [search]);
  const setControls = useCallback(
    (patch: ExploreControls) =>
      // A control REPLACES the entry and keeps what it remembers, so the depth of the zoom survives (Esc goes back exactly one level).
      setSearch((current) => withExploreControls(current, patch), {
        replace: true,
        state: location.state,
      }),
    [setSearch, location.state],
  );
  const zoomTo = useCallback(
    (zoom: Zoom, mode: "push" | "replace" = "push") =>
      setSearch((current) => withExploreControls(current, { zoom }), {
        replace: mode === "replace",
        state: { zoomDepth: mode === "replace" ? depth : depth + 1 },
      }),
    [setSearch, depth],
  );
  const clearZoom = useCallback(
    () =>
      setSearch((current) => withExploreControls(current, { zoom: null }), {
        state: { zoomDepth: 0 },
      }),
    [setSearch],
  );
  const goUp = useCallback(() => {
    if (address.zoom === null) return;
    if (depth > 0) window.history.back();
    else clearZoom();
  }, [address.zoom, depth, clearZoom]);
  return useMemo(
    () => ({ ...address, setControls, zoomTo, clearZoom, goUp, depth }),
    [address, setControls, zoomTo, clearZoom, goUp, depth],
  );
}
